import React, { useState, memo } from "react";
import { useUser, fetchWithAuth } from "../Context/UserContext";
import API_URL from "../config";
import { Plus } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";

const UserDescription = memo(
  ({ currentDescription, isOwner, updateProfileBio }) => {
    const { setUser } = useUser(); // Usamos el contexto global de usuario si somos propietarios
    const [description, setDescription] = useState(currentDescription);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const maxChars = 2000; // Número máximo de caracteres permitidos

    const handleSave = async () => {
      if (!isOwner) {
        console.error("No tienes permiso para modificar este perfil.");
        return; // Evitar guardar si no es el propietario
      }
      try {
        const response = await fetchWithAuth(
          `${API_URL}/profile/update_description`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ description }),
          }
        );

        const data = await response.json();
        if (response.ok) {
          // Actualizar el estado global del usuario si somos el dueño
          setUser((prevUser) => ({
            ...prevUser,
            profile: {
              ...prevUser.profile,
              bio: description,
            },
          }));
          // Actualizar el estado local del perfil en el componente padre
          updateProfileBio(description);
          setIsModalOpen(false);
        } else {
          console.error("No eres dueño de este perfil", data.error);
        }
      } catch (error) {
        console.error("Error en la solicitud:", error);
      }
    };

    const handleCancel = () => {
      // Restaura la descripción original y cierra el modal
      setDescription(description || "");
      setIsModalOpen(false);
    };

    const handleChange = (e) => {
      if (e.target.value.length <= maxChars) {
        setDescription(e.target.value);
      }
    };

    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-foreground">Descripción:</h3>
            {isOwner && (
              <button
                onClick={() => setIsModalOpen(true)}
                className="text-muted-foreground hover:text-foreground"
                aria-label="Editar descripción"
              >
                <Plus className="h-4 w-4" />
              </button>
            )}
          </div>
          {description ? (
            <p
              className="mt-2 text-sm text-muted-foreground"
              dangerouslySetInnerHTML={{
                __html: description.replace(/\n/g, "<br>"), // 🔹 Convierte los saltos de línea en <br>
              }}
            />
          ) : isOwner ? (
            <p className="mt-2 text-sm text-muted-foreground">
              Registra una descripción propia haciendo click al signo '+' que sale en la esquina derecha
              de esta sección.
            </p>
          ) : (
            <p className="mt-2 text-sm text-muted-foreground">El usuario no ha dejado una descripción todavía.</p>
          )}

          <Dialog open={isModalOpen} onOpenChange={(open) => !open && handleCancel()}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Editar Descripción</DialogTitle>
              </DialogHeader>
              <textarea
                value={description}
                onChange={handleChange}
                placeholder="Escribe una breve descripción sobre ti..."
                maxLength={maxChars}
                rows={6}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
              />
              <div className="text-right text-xs text-muted-foreground">
                {description.length}/{maxChars}
              </div>
              <DialogFooter>
                <Button onClick={handleSave}>Guardar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>
    );
  }
);

export default UserDescription;
